import React, { useState } from "react";
import { passStatusLabel } from "../passStatusLabel";
import { formatPhoneForDisplay } from "../../../helpers/phoneUtils";
import QRCode from "react-qr-code";
import { Menu } from "@mantine/core";
import {
  Calendar, ChevronDown, ChevronUp, DoorOpen, Link2, LogIn, LogOut, MapPin, MessageCircle,
  MessageSquare, Printer, Share2, Check, Users, XCircle,
} from "lucide-react";
import type { BusinessAccess } from "../../../hooks/useBusinessAccessInvitations";
import { NotificationService } from "../../../services/notification";
import { getChannelLabel, getDestinationParts, isEventPass } from "../../../utils/invitationDisplay";
import { buildInvitationLink, useInvitationBaseUrl } from "../invitationLink";
import { HikPinPanel } from "./HikPinPanel";
import { PassPaymentRow } from "./PassPaymentRow";
import { isAwaitingPayment } from "../awaitingPayment";
import { useT, type MessageKey, type TFunction } from "../../../i18n/admin";
import { TIME_ZONE } from "../../../i18n/format";

/*
  Invitations drawer, "Access details": who, the next step as the main action,
  the visit at a glance, one Share menu (WhatsApp / copy link / print) and the
  guests. Status changes go back to the page through onAction with the same
  labels as the row menu ("Confirmed", "Check In", "Check Out", "Cancel"), so
  the page keeps its confirmation dialog and mutations.
*/

type Guest = NonNullable<BusinessAccess['main_guest']>;

interface Props {
  row: BusinessAccess;
  /** SAM-1320 (U35): the event this pass was created for, when it is one. */
  eventName?: string;
  /** Status actions available for the row, in priority order (from the page). */
  actions: string[];
  onAction: (action: string) => void;
  isMobile: boolean;
  /** Full guest card (ID, document, OCR) shown when a guest is expanded. */
  renderGuestDetail: (guest: Guest, title: string) => React.ReactNode;
}

// Action ids stay English (the page matches on them); only the label is translated.
const ACTION_UI: Record<string, { label: MessageKey; icon: React.ReactNode }> = {
  Confirmed: { label: 'invitation.detail.do.confirm', icon: <Check style={{ width: 18, height: 18 }} /> },
  'Check In': { label: 'invitation.detail.do.checkIn', icon: <LogIn style={{ width: 18, height: 18 }} /> },
  'Check Out': { label: 'invitation.detail.do.checkOut', icon: <LogOut style={{ width: 18, height: 18 }} /> },
};

const STATUS_HINT: Record<number, MessageKey> = {
  1: 'invitation.detail.hint.pending',
  2: 'invitation.detail.hint.confirmed',
  3: 'invitation.detail.hint.verified',
  4: 'invitation.detail.hint.checkedIn',
  5: 'invitation.detail.hint.checkedOut',
  6: 'invitation.detail.hint.cancelled',
  7: 'invitation.detail.hint.expired',
};

const STATUS_STYLE: Record<string, { bg: string; color: string; border: string }> = {
  pending: { bg: 'rgba(255,183,77,.15)', color: '#B45309', border: 'rgba(255,183,77,.35)' },
  confirmed: { bg: 'rgba(34,197,94,.15)', color: '#15803D', border: 'rgba(34,197,94,.3)' },
  verified: { bg: 'rgba(59,130,246,.15)', color: '#1D4ED8', border: 'rgba(59,130,246,.3)' },
  'checked in': { bg: 'rgba(20,184,166,.15)', color: '#0F766E', border: 'rgba(20,184,166,.3)' },
  'checked out': { bg: 'rgba(168,85,247,.15)', color: '#7C3AED', border: 'rgba(168,85,247,.3)' },
  cancel: { bg: 'rgba(239,68,68,.12)', color: '#DC2626', border: 'rgba(239,68,68,.3)' },
};

const fullName = (g?: Guest | null) => `${g?.name || ''} ${g?.last_name || ''}`.trim();
const initials = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?';

/** YYYY-MM-DD of a value in the tenant's zone; a bare date is already a calendar day. */
const dayKey = (v: string) =>
  /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : new Intl.DateTimeFormat('en-CA', { timeZone: TIME_ZONE }).format(new Date(v));

// Calendar days in the tenant's zone (fmt.date), month written out — never dd/mm.
export const formatVisitDates = (
  start: string | null | undefined,
  end: string | null | undefined,
  t: TFunction,
  date: (v: string) => string,
): { main: string; sub: string } => {
  const s = start ? new Date(start) : null;
  if (!start || !s || isNaN(s.getTime())) return { main: t('invitation.detail.noDate'), sub: '' };
  const e = end ? new Date(end) : null;
  const startDay = date(start);
  const endDay = end && e && !isNaN(e.getTime()) ? date(end) : startDay;
  if (endDay === startDay) return { main: startDay, sub: t('invitation.detail.oneDay') };
  const days = Math.round((Date.parse(dayKey(end as string)) - Date.parse(dayKey(start))) / 86400000) + 1;
  return { main: `${startDay} – ${endDay}`, sub: Number.isFinite(days) && days > 1 ? t('invitation.detail.days', { count: days }) : '' };
};

/** WhatsApp only takes digits, country code included. */
const whatsappNumber = (tel?: string | null) => (tel || '').replace(/\D/g, '');

/** `sms:` wants the number in international form; the body separator that both iOS and Android
 * accept is `?&body=` (iOS ignores a bare `?body=` when the number is present). */
const smsNumber = (tel?: string | null) => {
  const digits = whatsappNumber(tel);
  return digits ? `+${digits}` : '';
};

const sectionTitle: React.CSSProperties = {
  fontSize: 12, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase',
  color: 'var(--ink-500)', margin: '0 0 12px',
};

const Fact = ({ icon, label, value, sub, wide }: { icon: React.ReactNode; label: string; value: React.ReactNode; sub?: string; wide?: boolean }) => (
  <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', minWidth: 0, gridColumn: wide ? '1 / -1' : undefined }}>
    <div style={{ width: 32, height: 32, borderRadius: 'var(--r-8)', background: 'var(--line-subtle)', display: 'grid', placeItems: 'center', color: 'var(--ink-500)', flex: 'none' }}>
      {icon}
    </div>
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 12, color: 'var(--ink-500)' }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink-900)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--ink-500)' }}>{sub}</div>}
    </div>
  </div>
);

export const InvitationDetailView = ({ row, eventName, actions, onAction, isMobile, renderGuestDetail }: Props) => {
  const baseUrl = useInvitationBaseUrl();
  const { t, fmt } = useT();
  const [openGuest, setOpenGuest] = useState<number | null>(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const pad = isMobile ? 16 : 24;

  const isBusiness = row.entity === 'business_access';
  const statusId = Number(row.business_access_status_id || row.status_id || 0);
  // S-38: a cancelled invitation shows no QR, Share or link (the backend refuses it too).
  const isCancelled = statusId === 6;
  const statusName = row.status_name || '';
  // SAM-1293: a pass waiting for its payment link gives no access: no QR, Share or door PIN until it is paid.
  const awaitingPayment = isBusiness && isAwaitingPayment(statusName);
  const linkExpiresAt = row.payment_link?.expires_at as string | undefined;
  const statusStyle = STATUS_STYLE[statusName.toLowerCase()] || { bg: 'var(--line-subtle)', color: 'var(--ink-700)', border: 'var(--line)' };

  const main = row.main_guest || null;
  const name = fullName(main) || t('invitation.detail.guestFallback');
  const companions: Guest[] = (row.business_guest as Guest[] | undefined) || [];
  const adults = 1 + companions.length;
  const children = Number(row.children_count || 0);
  const party = [
    adults === 1 ? t('invitation.detail.adult') : t('invitation.detail.adults', { count: adults }),
    children ? (children === 1 ? t('invitation.detail.child') : t('invitation.detail.children', { count: children })) : '',
  ].filter(Boolean).join(', ');

  const { primary: destination, secondary: destinationContext } = getDestinationParts(row, eventName);
  const visit = formatVisitDates(row.start_date, row.end_date, t, fmt.date);
  const channel = getChannelLabel(row);
  const gates = (() => {
    const rules = row.gate_rules as { external?: string; internal?: string } | string | undefined;
    const value = typeof rules === 'object' ? rules?.external || rules?.internal : rules;
    return !value || value === 'all' ? t('invitation.detail.allGates') : value;
  })();

  const code = row.legacy_code || row.code || row.id;
  const link = buildInvitationLink(baseUrl, row.code || row.id, isBusiness);

  const primaryAction = actions.find((a) => a !== 'Cancel');
  const secondaryActions = actions.filter((a) => a !== primaryAction && a !== 'Cancel');
  const canCancel = actions.includes('Cancel');

  const copyLink = () => {
    if (!link) return NotificationService.error(t('invitation.detail.noLink'));
    navigator.clipboard.writeText(link);
    NotificationService.success(t('invitation.detail.linkCopied'));
  };
  const shareText = () => t('invitation.detail.whatsappText', { name: main?.name || '', link }).replace(/\s+/g, ' ');
  const sendWhatsapp = () => {
    window.open(`https://wa.me/${whatsappNumber(main?.tel)}?text=${encodeURIComponent(shareText())}`, '_blank', 'noopener');
  };
  const sendSms = () => {
    window.location.href = `sms:${smsNumber(main?.tel)}?&body=${encodeURIComponent(shareText())}`;
  };

  const actionPanel = (
    <div>
      <div style={{ fontSize: 12, color: 'var(--ink-500)', marginBottom: 8 }}>{STATUS_HINT[statusId] ? t(STATUS_HINT[statusId]) : passStatusLabel(statusName)}</div>
      {primaryAction && (
        <button
          type="button"
          className="btn primary"
          onClick={() => onAction(primaryAction)}
          style={{ width: '100%', height: 48, fontSize: 16, fontWeight: 600, gap: 10 }}
        >
          {ACTION_UI[primaryAction]?.icon}
          {ACTION_UI[primaryAction] ? t(ACTION_UI[primaryAction].label) : primaryAction}
        </button>
      )}
      {(secondaryActions.length > 0 || canCancel) && (
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          {secondaryActions.map((a) => (
            <button key={a} type="button" className="btn ghost" onClick={() => onAction(a)} style={{ flex: 1, height: isMobile ? 44 : 40, fontSize: 14 }}>
              {ACTION_UI[a] ? t(ACTION_UI[a].label) : a}
            </button>
          ))}
          {canCancel && (
            <button
              type="button"
              className="btn danger"
              onClick={() => onAction('Cancel')}
              style={{ flex: 1, height: isMobile ? 44 : 40, fontSize: 14, gap: 8 }}
            >
              <XCircle size={16} />
              {isMobile ? t('invitation.detail.cancelShort') : t('invitation.detail.cancelInvitation')}
            </button>
          )}
        </div>
      )}
    </div>
  );
  const hasActions = actions.length > 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {/* Who */}
        <div style={{ padding: `${isMobile ? 16 : 20}px 56px 16px ${pad}px`, borderBottom: '1px solid var(--line)' }}>
          <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
            <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'var(--line-subtle)', color: 'var(--tenant-primary, #111827)', display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: 16, flex: 'none' }}>
              {initials(name)}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--ink-900)', lineHeight: 1.2 }}>{name}</div>
              <div style={{ fontSize: 13, color: 'var(--ink-500)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {[main?.tel ? formatPhoneForDisplay(main.tel) : '', main?.email].filter(Boolean).join(' · ')}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 12 }}>
            {statusName && (
              <span style={{ fontSize: 12, fontWeight: 600, borderRadius: 20, padding: '4px 10px', border: `1px solid ${statusStyle.border}`, background: statusStyle.bg, color: statusStyle.color, textTransform: 'capitalize' }}>
                {passStatusLabel(statusName)}
              </span>
            )}
            {[isEventPass(row) ? t('invitation.guestType.event') : isBusiness ? t('invitation.detail.business') : t('invitation.detail.residential'), row.type_name, channel].filter(Boolean).map((t) => (
              <span key={t} style={{ fontSize: 12, color: 'var(--ink-700)', background: 'var(--line-subtle)', borderRadius: 20, padding: '4px 10px' }}>{t}</span>
            ))}
          </div>
        </div>

        {/* The payment has its own status, apart from the pass's (business passes only). */}
        {isBusiness && row.id != null && <PassPaymentRow passId={String(row.id)} businessId={row.business_id} pad={pad} />}

        {/* Next step — at the top on desktop; pinned to the bottom on phones */}
        {!isMobile && hasActions && (
          <div style={{ padding: `16px ${pad}px`, borderBottom: '1px solid var(--line)', background: 'var(--surface)' }}>{actionPanel}</div>
        )}

        {/* Visit */}
        <div style={{ padding: `20px ${pad}px`, borderBottom: '1px solid var(--line)' }}>
          <h4 style={sectionTitle}>{t('invitation.detail.visit')}</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 14 }}>
            <Fact wide icon={<MapPin size={16} />} label={t('invitation.detail.whereTo')} value={destination || '--'} sub={destinationContext} />
            <Fact icon={<Calendar size={16} />} label={t('invitation.detail.when')} value={visit.main} sub={visit.sub} />
            <Fact icon={<Users size={16} />} label={t('invitation.detail.party')} value={party} />
            <Fact icon={<DoorOpen size={16} />} label={t('invitation.detail.gates')} value={gates} />
          </div>
        </div>

        {/* Pass + Share. A cancelled pass has no QR, no Share and no door PIN: the whole section goes, title included. */}
        {awaitingPayment && (
          <div data-testid="awaiting-payment" style={{ padding: `20px ${pad}px`, borderBottom: '1px solid var(--line)' }}>
            <h4 style={sectionTitle}>{t('invitation.detail.pass')}</h4>
            <div style={{ fontSize: 14, color: 'var(--ink-700)' }}>
              {linkExpiresAt
                ? t('invitation.detail.awaitingPaymentExpires', { when: `${fmt.date(linkExpiresAt)} · ${fmt.time(linkExpiresAt)}` })
                : t('invitation.detail.awaitingPayment')}
            </div>
          </div>
        )}
        {!isCancelled && !awaitingPayment && (
        <div style={{ padding: `20px ${pad}px`, borderBottom: '1px solid var(--line)' }}>
          <h4 style={sectionTitle}>{t('invitation.detail.pass')}</h4>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', border: '1px solid var(--line)', borderRadius: 'var(--r-12)', padding: 12 }}>
            <div style={{ width: 96, height: 96, flex: 'none', background: '#fff' }}>
              <QRCode value={String(code || 'no-code')} size={96} level="H" style={{ width: 96, height: 96 }} />
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 13, color: 'var(--ink-500)', marginBottom: 6 }}>{t('invitation.detail.passHint')}</div>
              <div style={{ fontFamily: 'var(--mono, monospace)', fontSize: 12, color: 'var(--ink-700)', wordBreak: 'break-all', marginBottom: 10 }}>{code}</div>
              <Menu withinPortal position="bottom-start" shadow="sm" zIndex={20000}>
                <Menu.Target>
                  <button type="button" className="btn ghost" style={{ gap: 8, fontSize: 14 }}>
                    <Share2 size={16} />
                    {t('invitation.detail.share')}
                  </button>
                </Menu.Target>
                <Menu.Dropdown>
                  {whatsappNumber(main?.tel) && link && (
                    // i18n-ignore: brand name
                    <Menu.Item icon={<MessageCircle size={16} color="#16A34A" />} onClick={sendWhatsapp}>WhatsApp</Menu.Item>
                  )}
                  {smsNumber(main?.tel) && link && (
                    <Menu.Item icon={<MessageSquare size={16} />} onClick={sendSms}>{t('invitation.detail.sms')}</Menu.Item>
                  )}
                  <Menu.Item icon={<Link2 size={16} />} onClick={copyLink}>{t('invitation.detail.copyLink')}</Menu.Item>
                  <Menu.Item icon={<Printer size={16} />} onClick={() => window.print()}>{t('invitation.detail.printPass')}</Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <HikPinPanel row={row} />
          </div>
        </div>
        )}

        {/* Guests */}
        <div style={{ padding: `20px ${pad}px`, borderBottom: '1px solid var(--line)' }}>
          <h4 style={sectionTitle}>{t('invitation.detail.guests', { count: 1 + companions.length })}</h4>
          {[main, ...companions].filter(Boolean).map((g, i) => {
            const guest = g as Guest;
            const gName = fullName(guest) || '--';
            const open = openGuest === i;
            return (
              <div key={guest.id || i} style={{ borderTop: i ? '1px solid var(--line)' : 'none' }}>
                <button
                  type="button"
                  onClick={() => setOpenGuest(open ? null : i)}
                  aria-expanded={open}
                  style={{ width: '100%', display: 'flex', gap: 12, alignItems: 'center', padding: '10px 0', background: 'none', border: 0, cursor: 'pointer', textAlign: 'left', font: 'inherit' }}
                >
                  <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--line-subtle)', display: 'grid', placeItems: 'center', fontWeight: 600, fontSize: 13, color: 'var(--ink-700)', flex: 'none' }}>
                    {initials(gName)}
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 500, color: 'var(--ink-900)' }}>{gName}</div>
                    <div style={{ fontSize: 12, color: 'var(--ink-500)' }}>{i === 0 ? t('invitation.detail.mainGuest') : t('invitation.detail.companion', { n: i })}</div>
                  </div>
                  <span style={{ fontSize: 12, color: guest.photo_url ? '#15803D' : '#B45309' }}>{guest.photo_url ? t('invitation.detail.idOnFile') : t('invitation.detail.noId')}</span>
                  {open ? <ChevronUp size={16} color="var(--ink-500)" /> : <ChevronDown size={16} color="var(--ink-500)" />}
                </button>
                {open && <div style={{ paddingBottom: 8 }}>{renderGuestDetail(guest, i === 0 ? t('invitation.field.mainGuest') : t('invitation.detail.companion', { n: i }))}</div>}
              </div>
            );
          })}
        </div>

        {/* Secondary details */}
        <button
          type="button"
          onClick={() => setMoreOpen((v) => !v)}
          aria-expanded={moreOpen}
          style={{ width: '100%', border: 0, background: 'none', padding: `14px ${pad}px`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', font: 'inherit', fontWeight: 500, color: 'var(--ink-700)', cursor: 'pointer' }}
        >
          {t('invitation.detail.moreDetails')} {moreOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
        {moreOpen && (
          <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 16px', padding: `0 ${pad}px 16px`, margin: 0, fontSize: 13 }}>
            {row.create_date && (<><dt style={{ color: 'var(--ink-500)' }}>{t('invitation.detail.created')}</dt><dd style={{ margin: 0, textAlign: 'right' }}>{fmt.date(row.create_date)}</dd></>)}
            {channel && (<><dt style={{ color: 'var(--ink-500)' }}>{t('invitation.detail.channel')}</dt><dd style={{ margin: 0, textAlign: 'right' }}>{channel}</dd></>)}
            <dt style={{ color: 'var(--ink-500)' }}>{t('invitation.detail.passCode')}</dt><dd style={{ margin: 0, textAlign: 'right', wordBreak: 'break-all' }}>{code}</dd>
          </dl>
        )}
      </div>

      {isMobile && hasActions && (
        <div style={{ borderTop: '1px solid var(--line)', padding: '12px 16px 16px', background: '#fff' }}>{actionPanel}</div>
      )}
    </div>
  );
};
